import React, { useState } from 'react';
import { Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import { AsyncPaginate } from 'react-select-async-paginate';
import { toast } from 'react-toastify';
import { Button } from '@components';
import HorizontalFieldsRow from '@app/components/common/HorizontalFieldsRow';
import Editor from '@app/components/editor/Editor';
import { FILE_MANAGER_API, TRICHYEU_API } from '@app/utils/apiUrl';
import { Get } from '@app/utils/httpProvider';
import { fetchWithAuth } from '@app/utils/httpClient';
import { URI_INTERNAL } from '@app/utils/pathLocations';

const toOption = (item) => ({
    value: item.id || item.Id,
    label: [item.codeTrichYeu || item.CodeTrichYeu, item.soQuyenDinh || item.SoQuyenDinh].filter(Boolean).join(' - ')
});

const loadTrichYeu = async (search, loadedOptions, additional) => {
    const page = additional?.page || 1;
    const result = await Get(TRICHYEU_API.GET_PAGING, null, { Keyword: search, PageIndex: page, PageSize: 20 });
    if (!result) throw new Error('Không tải được danh sách trích yếu');
    const items = result.data?.$values || result.data || [];
    return {
        options: items.map(toOption),
        hasMore: page * 20 < (result.totalRecords || 0),
        additional: { page: page + 1 }
    };
};

const ChungNhanForm = ({ formik, isSaving, isEdit }) => {
    const history = useHistory();
    const [isUploading, setIsUploading] = useState(false);
    const [fileName, setFileName] = useState('');
    const [selectedTrichYeu, setSelectedTrichYeu] = useState(null);
    const { handleSubmit, handleChange, values, errors, setFieldValue } = formik;
    const currentTrichYeu = useQuery(['trichYeu', values.trichYeuId], () => Get(TRICHYEU_API.GET_ITEM, values.trichYeuId), {
        enabled: !!values.trichYeuId
    });

    const uploadFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        setIsUploading(true);
        try {
            const body = new FormData();
            body.append('file', file);
            const response = await fetchWithAuth(FILE_MANAGER_API.UPLOAD, { method: 'POST', body });
            if (!response?.ok) throw new Error('Không tải được file chứng nhận');
            const data = await response.json();
            const uploaded = (data.$values || data)[0];
            const id = uploaded?.id || uploaded?.Id;
            if (!id) throw new Error('Không nhận được thông tin file chứng nhận');
            await setFieldValue('fileManagerId', id);
            setFileName(file.name);
        } catch (error) {
            toast.error(error.message || 'Tải file thất bại, vui lòng thử lại');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Form noValidate onSubmit={handleSubmit}>
            <HorizontalFieldsRow fields={[
                { label: 'Tên người nhận', name: 'tenNguoiNhan', value: values.tenNguoiNhan, onChange: handleChange, isInvalid: errors.tenNguoiNhan, error: errors.tenNguoiNhan },
                { label: 'Địa chỉ người nhận', name: 'diaChiNguoiNhan', value: values.diaChiNguoiNhan, onChange: handleChange, isInvalid: errors.diaChiNguoiNhan, error: errors.diaChiNguoiNhan }
            ]} />
            <Form.Group as={Row} className="mb-3">
                <Form.Label htmlFor="chungNhanTrichYeu" className="col-sm-2 col-form-label">Trích yếu</Form.Label>
                <Col sm="10">
                    <AsyncPaginate
                        inputId="chungNhanTrichYeu"
                        value={values.trichYeuId ? (selectedTrichYeu?.value === values.trichYeuId ? selectedTrichYeu : currentTrichYeu.data ? toOption(currentTrichYeu.data) : { value: values.trichYeuId, label: 'Đang tải trích yếu...' }) : null}
                        loadOptions={loadTrichYeu}
                        additional={{ page: 1 }}
                        debounceTimeout={300}
                        isClearable
                        placeholder="Chọn trích yếu"
                        onChange={(option) => {
                            setSelectedTrichYeu(option);
                            setFieldValue('trichYeuId', option?.value || '');
                        }}
                    />
                    {errors.trichYeuId && <div className="text-danger">{errors.trichYeuId}</div>}
                </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
                <Form.Label htmlFor="chungNhanFile" className="col-sm-2 col-form-label">File chứng nhận</Form.Label>
                <Col sm="10">
                    <Form.Control id="chungNhanFile" type="file" onChange={uploadFile} disabled={isUploading || isSaving} isInvalid={!!errors.fileManagerId} />
                    <Form.Control.Feedback type="invalid">{errors.fileManagerId}</Form.Control.Feedback>
                    {isUploading ? <div>Đang tải file...</div> : values.fileManagerId && <Form.Text>{fileName || 'Đã có file chứng nhận. Chọn file mới để thay thế.'}</Form.Text>}
                </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
                <Form.Label className="col-sm-2 col-form-label">Mô tả</Form.Label>
                <Col sm="10">
                    <Editor name="description" value={values.description} onChange={(value) => setFieldValue('description', value)} />
                </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
                <Col md={{ span: 10, offset: 2 }}>
                    <ButtonGroup>
                        <Button type="submit" theme="primary" disabled={isSaving || isUploading}>{isEdit ? 'Cập nhật' : 'Thêm'}</Button>
                        &nbsp;&nbsp;&nbsp;
                        <Button theme="secondary" onClick={() => history.push(URI_INTERNAL.CHUNGNHAN_PATH)}>Hủy</Button>
                    </ButtonGroup>
                </Col>
            </Form.Group>
        </Form>
    );
};

export default ChungNhanForm;
